// Local-only form-draft persistence. Solves the "I picked images, refreshed,
// work is gone" problem on the add-sample form (and is reusable for any
// other form where unsaved work would otherwise be lost on refresh).
//
// Split storage by data shape:
//   • JSON-serializable values (text, dropdowns, tag IDs)  → localStorage
//   • File / Blob objects (image pickers)                  → IndexedDB
//
// Persists on every change with a 250ms debounce so we're not writing on
// every keystroke. Restores once on mount. Clears explicitly via
// `clearDraft()` after a successful submit (or when the user hits Cancel
// and confirms they want to discard).
//
// Drafts are scoped by a `formId` so /library/new and /library/{uuid}/edit
// don't clobber each other, and two different sample edits each keep their
// own draft. Bumping `version` invalidates any cached drafts that don't
// match — bump when the form schema changes.
//
// Reliability hardening (2026-05-04 audit):
//   • `beforeunload` listener flushes the latest values synchronously
//     before tab close / refresh — without it, anything typed inside the
//     250 ms debounce window is lost to a sudden reload.
//   • Drafts older than `STALE_AFTER_MS` (14 days) auto-discard on
//     restore so a months-old draft doesn't haunt the user.
//   • Best-effort `navigator.storage.persist()` request so the browser
//     keeps our IDB data under storage pressure.

import { useEffect, useRef, useState } from "react";

import { idbPutBlob, idbGetBlob, idbDeleteByPrefix } from "@/lib/idb";

const DRAFT_PREFIX = "cc-os-form-draft:";
const STALE_AFTER_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// Best-effort: ask the browser to keep our IDB data even under storage
// pressure. Fires once per page load. No-op if the API is missing or the
// permission is denied — drafts still work, they're just evictable.
let persistRequested = false;
function requestPersistedStorage(): void {
  if (persistRequested) return;
  persistRequested = true;
  try {
    if (typeof navigator !== "undefined" && navigator.storage && navigator.storage.persist) {
      void navigator.storage.persist().catch(() => { /* ignore */ });
    }
  } catch {
    /* ignore */
  }
}

interface DraftEnvelope<T> {
  version: number;
  values: T;
  fileMeta: Record<string, { name: string; type: string; size: number } | null>;
  savedAt: number;
}

interface UseFormDraftOptions<T extends Record<string, unknown>> {
  /** Unique per form route. Examples: "library:new", `library:edit:${id}`,
   *  `supplier:new`. Two forms with the same id share a draft, so make
   *  this collision-free. */
  formId: string;
  /** Bump when the form schema changes incompatibly. Older drafts get
   *  silently discarded on restore. */
  version: number;
  /** Current form values (typically from RHF's watch()). */
  values: T;
  /** Apply restored values back to the form (typically RHF's reset()).
   *  Called once on mount when a draft is found. */
  setValues: (v: T) => void;
  /** File slots managed outside RHF (the front / back image pickers).
   *  Key = slot id (e.g. "front"), value = current File or null. */
  fileSlots?: Record<string, File | null>;
  /** Apply restored files back to component state. Called once on mount
   *  per slot when a draft file is found. */
  setFile?: (slotId: string, file: File | null) => void;
  /** When false, the hook does nothing (useful while edit-mode is still
   *  loading the row). Default true. */
  enabled?: boolean;
}

interface UseFormDraftResult {
  /** True after the mount-time restore has finished. */
  ready: boolean;
  /** True if values / files were actually restored from a previous draft. */
  restored: boolean;
  /** Drop the draft from localStorage + IndexedDB. Call after a
   *  successful submit, or from a "Discard draft" button. */
  clearDraft: () => Promise<void>;
}

/**
 * Persist form state to localStorage + IndexedDB and restore it on mount.
 *
 * Wiring example (add-sample form):
 *
 *   const watched = watch();
 *   const draft = useFormDraft({
 *     formId: mode === "edit" ? `library:edit:${id}` : "library:new",
 *     version: 1,
 *     values: watched,
 *     setValues: reset,
 *     fileSlots: { front: frontFile, back: backFile },
 *     setFile: (slot, f) => slot === "front" ? setFrontFile(f) : setBackFile(f),
 *     enabled: !loading,  // wait until the edit-mode row finishes loading
 *   });
 *
 *   // After a successful submit:
 *   await draft.clearDraft();
 */
export function useFormDraft<T extends Record<string, unknown>>(
  opts: UseFormDraftOptions<T>,
): UseFormDraftResult {
  const { formId, version, values, setValues, fileSlots, setFile, enabled = true } = opts;
  const valuesKey = `${DRAFT_PREFIX}${formId}`;
  const blobKeyPrefix = `${DRAFT_PREFIX}${formId}::`;

  const [ready, setReady] = useState(false);
  const [restored, setRestored] = useState(false);
  // Block the save effect until restore completes, otherwise the first
  // render's empty `values` would overwrite the draft we're about to read.
  const restoredRef = useRef(false);

  // ── Restore once on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    requestPersistedStorage();
    let cancelled = false;
    const run = async () => {
      try {
        const raw = localStorage.getItem(valuesKey);
        if (!raw) {
          restoredRef.current = true;
          setReady(true);
          return;
        }
        const parsed = JSON.parse(raw) as DraftEnvelope<T>;
        if (parsed.version !== version) {
          // Schema changed under us; toss the draft.
          localStorage.removeItem(valuesKey);
          await idbDeleteByPrefix(blobKeyPrefix);
          restoredRef.current = true;
          setReady(true);
          return;
        }
        // TTL check — drafts older than STALE_AFTER_MS are silently
        // discarded so the user doesn't get spooked by a months-old
        // draft surfacing the next time they open this form.
        const age = Date.now() - (typeof parsed.savedAt === "number" ? parsed.savedAt : 0);
        if (age > STALE_AFTER_MS) {
          localStorage.removeItem(valuesKey);
          await idbDeleteByPrefix(blobKeyPrefix);
          restoredRef.current = true;
          setReady(true);
          return;
        }
        // Restore JSON values first so RHF's reset() sees the full shape
        // (otherwise undefined fields default to "" / null and the user
        // sees a flicker).
        setValues(parsed.values);

        // Restore file slots second. Each slot's blob lives under
        // `${blobKeyPrefix}${slotId}` in IndexedDB.
        if (fileSlots && setFile) {
          for (const slotId of Object.keys(fileSlots)) {
            const meta = parsed.fileMeta?.[slotId];
            if (!meta) continue;
            const blob = await idbGetBlob(`${blobKeyPrefix}${slotId}`);
            if (cancelled) return;
            if (!blob) continue;
            // Re-wrap as File so downstream upload code (which calls
            // file.name etc.) keeps working unchanged.
            const file = new File([blob], meta.name, {
              type: meta.type || blob.type,
              lastModified: parsed.savedAt,
            });
            setFile(slotId, file);
          }
        }

        if (cancelled) return;
        setRestored(true);
      } catch (err) {
        console.warn("useFormDraft restore failed", err);
      } finally {
        if (!cancelled) {
          restoredRef.current = true;
          setReady(true);
        }
      }
    };
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesKey, enabled]);

  // ── Save on change (debounced) ─────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (!restoredRef.current) return;
    const timer = setTimeout(() => {
      try {
        const fileMeta: Record<string, { name: string; type: string; size: number } | null> = {};
        if (fileSlots) {
          for (const [slotId, file] of Object.entries(fileSlots)) {
            fileMeta[slotId] = file
              ? { name: file.name, type: file.type, size: file.size }
              : null;
          }
        }
        const envelope: DraftEnvelope<T> = {
          version,
          values,
          fileMeta,
          savedAt: Date.now(),
        };
        localStorage.setItem(valuesKey, JSON.stringify(envelope));

        // Persist Blobs separately so the localStorage entry stays small.
        // Files don't change often (only on user pick), so we can spam-write
        // them on every save without hurting much, but skip if we know the
        // slot is null to avoid useless transactions.
        if (fileSlots) {
          void Promise.all(
            Object.entries(fileSlots).map(async ([slotId, file]) => {
              if (file) await idbPutBlob(`${blobKeyPrefix}${slotId}`, file);
            }),
          );
        }
      } catch (err) {
        // Quota exceeded / private-mode Safari / etc. — non-fatal.
        console.warn("useFormDraft save failed", err);
      }
    }, 250);
    return () => clearTimeout(timer);
    // Stringify-compare values so identical re-renders don't spam writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), JSON.stringify(serializeFileSlots(fileSlots)), enabled]);

  // ── beforeunload sync flush ────────────────────────────────────────────────
  // The 250 ms debounce above means anything typed in the last quarter
  // second before a tab close / reload is lost. localStorage.setItem is
  // synchronous and DOES run inside beforeunload, so we use it as a
  // last-ditch flush. IDB blob writes are NOT flushed here — they're
  // async-only — but the blob itself was already saved on the previous
  // user interaction (file pick), so missing the latest debounce window
  // only loses text/dropdown deltas, never images.
  const latestRef = useRef<{ values: T; fileSlots?: Record<string, File | null> }>({
    values, fileSlots,
  });
  // Refresh after commit rather than during render — the only reader is the
  // beforeunload/pagehide handler below, which can never fire mid-render, so
  // the post-commit write is equivalent and keeps `react-hooks/refs` happy.
  useEffect(() => {
    latestRef.current = { values, fileSlots };
  });
  useEffect(() => {
    if (!enabled) return;
    const handler = () => {
      if (!restoredRef.current) return;
      try {
        const snapshot = latestRef.current;
        const fileMeta: Record<string, { name: string; type: string; size: number } | null> = {};
        if (snapshot.fileSlots) {
          for (const [slotId, file] of Object.entries(snapshot.fileSlots)) {
            fileMeta[slotId] = file
              ? { name: file.name, type: file.type, size: file.size }
              : null;
          }
        }
        const envelope: DraftEnvelope<T> = {
          version,
          values: snapshot.values,
          fileMeta,
          savedAt: Date.now(),
        };
        localStorage.setItem(valuesKey, JSON.stringify(envelope));
      } catch {
        // Quota / private mode — best-effort, swallow.
      }
    };
    window.addEventListener("beforeunload", handler);
    // pagehide fires on iOS Safari where beforeunload doesn't, and on
    // bfcache navigations — covers the same intent.
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
     
  }, [valuesKey, enabled, version]);

  const clearDraft = async () => {
    try {
      localStorage.removeItem(valuesKey);
    } catch { /* localStorage not available */ }
    await idbDeleteByPrefix(blobKeyPrefix);
  };

  return { ready, restored, clearDraft };
}

// Lightweight identity for files so the save-effect dep array is stable
// when the user hasn't actually changed a slot.
function serializeFileSlots(
  slots: Record<string, File | null> | undefined,
): Record<string, { name: string; size: number } | null> {
  if (!slots) return {};
  const out: Record<string, { name: string; size: number } | null> = {};
  for (const [k, f] of Object.entries(slots)) {
    out[k] = f ? { name: f.name, size: f.size } : null;
  }
  return out;
}
